import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { usePermission } from '@/hooks/usePermission'
import { useAssistantWindowInternal } from '@/components/ai-chat/assistantWindowContext'
import { DOCK_MIN_VIEWPORT, DOCK_WIDTH } from '@/features/ai-chat/windowGeometry'
import { useChat } from '../ChatProvider'
import { chatApi, isContactNotAllowed } from '../api/chatApi'
import { CHAT_PERMISSIONS } from '../permissions'
// 「最近查看的任务」模块挂上主动登出时清空的监听（Layout 常驻，保证登出时已注册）
import '../utils/recentTasks'
import {
  ChatPanelContext,
  type ChatPanelAboutTask,
  type ChatPanelApi,
  type ChatPanelLayout,
  type ChatPanelView,
  type MessageUserOptions,
} from './chatPanelContext'

// 侧边栏本体（会话列表 / 会话页 / 输入栏 …）打开后才加载，不进入口 chunk（同 AI 助手浮窗）
const ChatPanel = lazy(() => import('./ChatPanel').then((m) => ({ default: m.ChatPanel })))

/** 小屏（< 768px）：整屏，同 AI 助手 */
const MOBILE_QUERY = '(max-width: 767px)'

/** 页面 flyout 读它让出停靠宽度（与 AI 助手的 --assistant-dock-inset 相加；两者同一时间只有一个非 0） */
export const CHAT_PANEL_DOCK_INSET_VAR = '--chat-panel-dock-inset'
/** 侧边栏在右侧盖住的宽度（停靠 / 覆盖都算，整屏不算）：AI 助手最小化小条往左让 */
export const CHAT_PANEL_WIDTH_VAR = '--chat-panel-width'

/**
 * Messages 右侧内嵌侧边栏（与 AI 助手停靠面板同一套机制：同宽、同位置、页面让位，见 AssistantDockInset / flyout）。
 *
 * - 顶栏 Messages 按钮开合；侧边栏里「会话列表 ↔ 会话」像 mobile 一样栈式切换。
 * - 与 AI 助手**同一时间只开一个**：打开侧边栏时助手若开着就最小化（会话不丢，小条还在）；
 *   助手被打开 / 恢复时侧边栏关上。两个都要占右侧 420px，并排会把页面挤得太窄。
 * - 必须在 AssistantWindowProvider 与 ChatProvider 里面。
 */
export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const { enabled, me, upsert, subscribeList } = chat
  const { hasPermission } = usePermission()
  const canStartChat = enabled && hasPermission(CHAT_PERMISSIONS.read) && hasPermission(CHAT_PERMISSIONS.send)
  const assistant = useAssistantWindowInternal()
  const { minimize: minimizeAssistant } = assistant
  const assistantState = assistant.state.windowState

  const [isOpen, setOpen] = useState(false)
  const [view, setView] = useState<ChatPanelView>({ kind: 'list' })
  /** 从任务进单聊带来的任务，等会话页接走（只给那个会话） */
  const [aboutTask, setAboutTask] = useState<ChatPanelAboutTask | null>(null)
  const aboutSeq = useRef(0)
  const triggerRef = useRef<HTMLElement | null>(null)

  const wide = useMediaQuery(`(min-width: ${DOCK_MIN_VIEWPORT}px)`)
  const mobile = useMediaQuery(MOBILE_QUERY)
  const layout: ChatPanelLayout = mobile ? 'fullscreen' : wide ? 'dock' : 'overlay'
  const open = isOpen && enabled

  // 助手开着时打开侧边栏：助手最小化（不是关闭——关闭会结束它的会话）
  const assistantStateRef = useRef(assistantState)
  assistantStateRef.current = assistantState
  const show = useCallback((next: ChatPanelView) => {
    if (assistantStateRef.current === 'open') minimizeAssistant()
    setView(next)
    setOpen(true)
  }, [minimizeAssistant])

  // 助手被打开 / 恢复（用户点了它的入口）：侧边栏让位
  const prevAssistant = useRef(assistantState)
  useEffect(() => {
    const prev = prevAssistant.current
    prevAssistant.current = assistantState
    if (assistantState === 'open' && prev !== 'open') setOpen(false)
  }, [assistantState])

  // 换人 / 失去权限：收起并回到列表（不能留着上一个人的会话）。
  // 只在真的换了人 / 权限掉了时收：首次登录落地（null → 我）不收，否则深链刚打开的侧边栏会被关掉
  const prevMe = useRef(me)
  useEffect(() => {
    const changedUser = prevMe.current !== me && prevMe.current != null
    prevMe.current = me
    if (changedUser || !enabled) {
      setOpen(false)
      setView({ kind: 'list' })
      setAboutTask(null)
    }
  }, [me, enabled])

  // 侧边栏开着期间要会话列表（取 / 轮询 / 实时并进来）
  useEffect(() => (open ? subscribeList() : undefined), [open, subscribeList])

  // 打开别的会话 / 回列表 / 关闭：没接走的任务作废（不串到别的会话、下次打开也不冒出来）
  const openList = useCallback(() => {
    setAboutTask(null)
    show({ kind: 'list' })
  }, [show])
  const openConversation = useCallback((id: string) => {
    setAboutTask(null)
    show({ kind: 'thread', id })
  }, [show])
  const close = useCallback(() => {
    setAboutTask(null)
    setOpen(false)
  }, [])
  const toggle = useCallback(() => {
    setAboutTask(null)
    if (open) setOpen(false)
    else show({ kind: 'list' })
  }, [open, show])
  const consumeAboutTask = useCallback((seq: number) => {
    setAboutTask((cur) => (cur && cur.seq === seq ? null : cur))
  }, [])
  /** 打开某个会话；从任务进来的把任务交给它（每次一个新 seq） */
  const openConversationAbout = useCallback((id: string, options?: MessageUserOptions) => {
    const task = options?.aboutTask
    setAboutTask(task ? { conversationId: id, task, seq: ++aboutSeq.current } : null)
    show({ kind: 'thread', id })
  }, [show])

  // 关上后焦点回到顶栏按钮（键盘用户 Esc 关掉之后不掉到 body）
  const prevOpen = useRef(open)
  useEffect(() => {
    const was = prevOpen.current
    prevOpen.current = open
    if (was && !open) {
      const active = document.activeElement
      if (!active || active === document.body || active.closest('[data-chat-panel]')) triggerRef.current?.focus()
    }
  }, [open])

  const conversationsRef = useRef(chat.conversations)
  conversationsRef.current = chat.conversations
  /** 同一时间只发起一个单聊请求（连点头像图标不建两次请求） */
  const starting = useRef<string | null>(null)
  const messageUser = useCallback(async (userId: string, options?: MessageUserOptions): Promise<boolean> => {
    if (!canStartChat || !userId || userId === me) return false
    const existing = conversationsRef.current.find((c) => c.kind === 'direct' && c.peerUserId === userId)
    if (existing) {
      openConversationAbout(existing.id, options)
      return true
    }
    if (starting.current) return false
    starting.current = userId
    try {
      const conversation = await chatApi.createConversation({ kind: 'direct', memberIds: [userId] })
      upsert(conversation)
      openConversationAbout(conversation.id, options)
      return true
    } catch (err) {
      toast.error(isContactNotAllowed(err) ? t('contactNotAllowed') : t('createFailed'))
      return false
    } finally {
      starting.current = null
    }
  }, [canStartChat, me, openConversationAbout, t, upsert])

  const dockInset = open && layout === 'dock' ? DOCK_WIDTH : 0
  const coverWidth = open && layout !== 'fullscreen' ? DOCK_WIDTH : 0
  useEffect(() => {
    const root = document.documentElement
    if (dockInset) root.style.setProperty(CHAT_PANEL_DOCK_INSET_VAR, `${dockInset}px`)
    else root.style.removeProperty(CHAT_PANEL_DOCK_INSET_VAR)
    if (coverWidth) root.style.setProperty(CHAT_PANEL_WIDTH_VAR, `${coverWidth}px`)
    else root.style.removeProperty(CHAT_PANEL_WIDTH_VAR)
    return () => {
      root.style.removeProperty(CHAT_PANEL_DOCK_INSET_VAR)
      root.style.removeProperty(CHAT_PANEL_WIDTH_VAR)
    }
  }, [coverWidth, dockInset])

  const api = useMemo<ChatPanelApi>(() => ({
    open, view, layout, dockInset, canStartChat, me, openList, openConversation, close, toggle, messageUser,
    aboutTask, consumeAboutTask, triggerRef,
  }), [
    open, view, layout, dockInset, canStartChat, me, openList, openConversation, close, toggle, messageUser,
    aboutTask, consumeAboutTask,
  ])

  return (
    <ChatPanelContext.Provider value={api}>
      {children}
      {open ? (
        <Suspense fallback={null}>
          <ChatPanel />
        </Suspense>
      ) : null}
    </ChatPanelContext.Provider>
  )
}
